<template>
    <span>
        <!-- Usuário com 1 tenant não vê nada: não há o que trocar. -->
        <button
            v-if="tenants.length > 1"
            type="button"
            class="tenant-pill"
            aria-haspopup="dialog"
            :aria-label="`Empresa ativa: ${nomeAtivo}. Trocar de empresa`"
            :title="`Empresa ativa: ${nomeAtivo}`"
            @click="dialogVisivel = true"
        >
            <i class="pi pi-building" aria-hidden="true"></i>
            <span class="tenant-nome">{{ nomeAtivo }}</span>
        </button>

        <!-- Falhou ao resolver o tenant: o conteúdo continua bloqueado de
             propósito (`tenantPronto` no AppLayout), então sem este botão a
             tela ficava em branco para sempre — o toast expira em 8s e não
             sobra nenhum caminho de volta além do F5, que ninguém adivinha. -->
        <button
            v-else-if="erro"
            type="button"
            class="tenant-pill tenant-erro"
            :aria-label="`Não foi possível carregar a empresa ativa: ${erro}. Tentar de novo`"
            :title="erro"
            @click="carregar"
        >
            <i class="pi pi-refresh" aria-hidden="true"></i>
            <span>Tentar de novo</span>
        </button>

        <TenantSelectDialog
            v-model:visible="dialogVisivel"
            :tenants="tenants"
            :ativo-id="ativoId"
            :padrao-id="padraoId"
            :obrigatorio="!liberado"
            :salvando="salvando"
            @confirmar="trocar"
        />
    </span>
</template>

<script>
import TenantSelectDialog from './TenantSelectDialog.vue';
import Tenant from '@/service/tenant.js';
import Token from '@/utils/Token';

/**
 * Dono do tenant ativo no front (T4.2 + T4.4): carrega os autorizados, decide
 * se a seleção de entrada é obrigatória, persiste a troca e emite `pronto`
 * para o layout liberar o conteúdo.
 *
 * Enquanto `pronto` não sai, o layout não renderiza rota nenhuma: se a tela
 * de seleção fosse só um overlay, as listagens por trás já teriam buscado
 * dados do tenant que o backend escolheu por fallback — exatamente o dado
 * errado que o projeto existe para não mostrar.
 */
export default {
    name: 'TenantSwitcher',
    components: { TenantSelectDialog },
    emits: ['pronto'],
    data() {
        return {
            tenants: [],
            ativoId: null,
            padraoId: null,
            dialogVisivel: false,
            salvando: false,
            liberado: false,
            erro: null,
        };
    },
    computed: {
        nomeAtivo() {
            return this.tenants.find(t => t.id === this.ativoId)?.nome || 'Empresa';
        },
    },
    beforeMount() {
        this.carregar();
    },
    methods: {
        carregar() {
            this.erro = null;

            Tenant.meus()
                .then(({ data }) => {
                    this.tenants = data.data.tenants;
                    this.ativoId = data.data.tenant_ativo_id;
                    this.padraoId = data.data.tenant_padrao_id;
                    this.identificarAba();

                    // Só para quem tem escolha real E ainda não definiu
                    // preferência. Quem já definiu não é perguntado de novo: a
                    // tela existe para decidir, não para confirmar a cada
                    // login. Trocar continua no pill do header.
                    if (this.tenants.length > 1 && this.padraoId === null && !Token.tenantSelecionado()) {
                        this.dialogVisivel = true;
                        return;
                    }

                    this.liberar();
                })
                .catch(erro => {
                    // Sem saber qual é o tenant ativo, o conteúdo continua
                    // bloqueado de propósito. 403 aqui é usuário sem nenhuma
                    // empresa ativa — o middleware barra todo o resto igual.
                    this.erro = erro.response?.data?.message || erro.response?.data?.msg || erro.message;

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Ops!',
                        detail: this.erro,
                        life: 8000,
                    });
                });
        },
        /**
         * Corrige a aba para a empresa ATIVA.
         *
         * O `main.blade.php` escreve title e favicon com o tenant do endereço,
         * porque na hora de renderizar o servidor não sabe quem está logado —
         * a API é stateless e o token vive no `sessionStorage`. Com a
         * preferência acima do domínio, quem tem preferência GTS abrindo o
         * endereço da MAM ficava com a aba dizendo MAM e a grade mostrando GTS.
         *
         * A URL continua a mesma de propósito: trocar de empresa não é motivo
         * para mandar o usuário para outro endereço.
         */
        identificarAba() {
            const ativo = this.tenants.find(t => t.id === this.ativoId);

            if (!ativo) {
                return;
            }

            document.title = ativo.nome;

            if (!ativo.favicon) {
                return;
            }

            // Substitui o elemento em vez de trocar o `href`: navegador ignora
            // com frequência a mudança de href num <link rel=icon> já
            // processado, e a aba ficava com o ícone do endereço.
            document.querySelectorAll('link[rel="icon"]').forEach(el => el.remove());
            document.head.appendChild(
                Object.assign(document.createElement('link'), { rel: 'icon', href: ativo.favicon })
            );
        },

        liberar() {
            Token.marcarTenantSelecionado();
            this.dialogVisivel = false;
            this.liberado = true;
            this.$emit('pronto');
        },
        trocar({ tenantId, padrao }) {
            const mesmaEmpresa = tenantId === this.ativoId;

            // Nada mudou: nem a empresa da aba nem a preferência de entrada.
            if (mesmaEmpresa && padrao === (this.padraoId === tenantId)) {
                this.liberar();
                return;
            }

            this.salvando = true;
            Tenant.trocar(tenantId, padrao)
                .then(({ data }) => {
                    Token.marcarTenantSelecionado();
                    Token.fixarTenantAtalho(this.tenants.find(t => t.id === tenantId).slug);

                    // Só a preferência mudou: a aba já está na empresa certa e
                    // recarregar seria jogar fora a tela por nada.
                    if (mesmaEmpresa) {
                        this.padraoId = data.data.tenant_padrao_id;
                    this.identificarAba();
                        this.salvando = false;
                        this.liberar();
                        return;
                    }

                    // Recarga dura, e é uma escolha consciente: a SPA não tem
                    // store central para invalidar, cada página guarda o que
                    // buscou no próprio componente, e requisições disparadas
                    // antes da troca ainda voltam com dados do tenant anterior.
                    // Limpar isso à mão exigiria auditar todo componente novo
                    // para sempre; o reload descarta o heap inteiro e não tem
                    // como esquecer de um. O token vive em sessionStorage, que
                    // sobrevive ao reload — o usuário não é deslogado.
                    // Vai para /app/home, e não reload(), porque a rota atual
                    // pode ser o detalhe de um registro do tenant anterior.
                    window.location.assign('/app/home');
                })
                .catch(erro => {
                    this.salvando = false;
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Ops!',
                        detail: erro.response?.data?.msg || erro.message,
                        life: 5000,
                    });
                });
        },
    },
};
</script>

<style scoped>
.tenant-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 12px;
    margin-right: 12px;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    cursor: pointer;
    color: #8b949e;
    font-size: 13px;
    transition: background 140ms ease, color 140ms ease, border-color 140ms ease;
}
.tenant-pill:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #e6edf3;
    border-color: rgba(255, 255, 255, 0.25);
}
.tenant-nome {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
@media (max-width: 640px) {
    .tenant-nome { display: none; }
}
</style>
